import {createApp, defineAsyncComponent} from 'vue';
import mitt from 'mitt';
import * as KeyshapeJS from 'keyshapejs';
import LocaleSwitcher from '@/components/LocaleSwitcher';
import LocaleSuggester from '@/components/LocaleSuggester';
import Navigation from '@/components/Navigation';
import {plugin, defaultConfig, createInput} from '@formkit/vue'
import {generateClasses} from '@formkit/themes'
import {de, en} from '@formkit/i18n';
import formkitTheme from '@/formkit-theme';
import FormTeaser from '@/components/FormTeaser';
import translator from '@/utils/translator';
import within from "@/formkit-rules";
import {flare} from "@flareapp/js";
import {flareVue} from "@flareapp/vue";

const emitter = mitt();

// Async components that are also used with FormKit
const CheckboxCardGroup = defineAsyncComponent(() =>
    import(/* webpackChunkName: "chunks/form" */ '@/components/inputs/CheckboxCardGroup')
);

const EngineeringTriangle = defineAsyncComponent(() =>
    import(/* webpackChunkName: "chunks/form" */ '@/components/inputs/EngineeringTriangle')
);

const Lightswitch = defineAsyncComponent(() =>
    import(/* webpackChunkName: "chunks/form" */ '@/components/inputs/Lightswitch')
);

if (process.env.NODE_ENV === 'production') {
    flare.light();
}

const app = createApp({
    components: {
        CheckboxCardGroup,
        EngineeringTriangle,
        FormTeaser,
        Lightswitch,
        LocaleSwitcher,
        LocaleSuggester,
        Navigation,
        Gallery: defineAsyncComponent(() =>
            import('@/components/Gallery')
        ),
        Accordion: defineAsyncComponent(() =>
            import('@/components/Accordion')
        ),
        PunchlineSwitcher: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/PunchlineSwitcher')
        ),
        MapboxMap: defineAsyncComponent(() =>
            import('@/components/MapboxMap')
        ),
        TestimonialCarousel: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/TestimonialCarousel')
        ),
        CardStack: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/process" */ '@/components/CardStack')
        ),
        ContactForm: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/form" */ '@/components/ContactForm')
        ),
        ConsultingIcon: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/icons/ConsultingIcon')
        ),
        DesignIcon: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/icons/DesignIcon')
        ),
        DevelopmentIcon: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/icons/DevelopmentIcon')
        ),
        Galaxy: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/process" */ '@/components/Galaxy')
        ),
        ServiceIcon: defineAsyncComponent(() =>
            import(/* webpackChunkName: "chunks/home" */ '@/components/icons/ServiceIcon')
        ),
    },
}).use(
    plugin,
    defaultConfig({
        config: {
            classes: generateClasses(formkitTheme)
        },
        rules: {within},
        locales: {de, en},
        locale: document.documentElement.lang,
        inputs: {
            checkboxCardGroup: createInput(CheckboxCardGroup, {
                props: ['options', 'isSmall']
            }),
            engineeringTriangle: createInput(EngineeringTriangle, {
                props: ['options']
            }),
            lightswitch: createInput(Lightswitch, {
                props: ['description']
            }),
        },
    })
);

app.provide('emitter', emitter);
app.provide('translator', translator);
app.provide('keyshape', KeyshapeJS)
app.use(flareVue);
app.mount('#app')
